import { Reference, useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { refreshUserAccount } from '../services/account';
import * as briefsManager from '../services/briefs';
import { GET_BRIEFS_OVERVIEW, GET_BRIEF_DETAILS } from '../services/briefs';
import { Brief } from '../services/models/brief';
import { BriefCreationRequest } from '../services/models/brief-creation-request';
import { BriefKeywordType } from '../services/models/brief-keyword-type';
import { BriefLanguage } from '../services/models/brief-language';
import { DayOfWeek } from '../services/models/day-of-week';
import * as segment from '../services/segment';
import * as slackManager from '../services/slack';
import { useServiceErrorHandler } from './useServiceErrorHandler';

interface BriefsOverviewResult {
  briefs: Brief[];
}

// The briefs list, served from the Apollo cache. Brief entities are
// normalized by id, so mutations that return the changed fields (name,
// language, schedule, suggested stories) propagate to every consumer
// automatically; list- and array-shaped changes are applied with the cache
// updates below, mirroring the retired Redux briefs reducers.
export const useBriefsOverview = () => {
  const { data, loading, refetch } = useQuery<BriefsOverviewResult>(GET_BRIEFS_OVERVIEW, {
    fetchPolicy: 'cache-first',
  });

  return { briefs: data?.briefs, loading, refetch };
};

// Imperative network fetch of a brief's details; the normalized result
// merges into the cached Brief entity (used after the Slack popup completes,
// replacing the legacy requestBriefDetails thunk).
export const useRefreshBriefDetails = () => {
  const client = useApolloClient();
  const handleServiceError = useServiceErrorHandler();

  return useCallback(
    async (briefId: number) => {
      try {
        await client.query({
          query: GET_BRIEF_DETAILS,
          variables: { briefId },
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        handleServiceError(error);
      }
    },
    [client, handleServiceError]
  );
};

export const useCreateBrief = () => {
  const client = useApolloClient();
  const handleServiceError = useServiceErrorHandler();
  const [isCreating, setIsCreating] = useState(false);

  const createBrief = useCallback(
    async (creationRequest: BriefCreationRequest): Promise<Brief | undefined> => {
      setIsCreating(true);
      try {
        const newBrief = await briefsManager.createBrief(creationRequest);

        client.cache.updateQuery<BriefsOverviewResult>({ query: GET_BRIEFS_OVERVIEW }, data =>
          data ? { briefs: [...data.briefs, newBrief] } : data
        );

        // Same event the redux-segment middleware emitted on
        // BriefsAction.CompleteCreating.
        segment.trackEvent('Brief Created', { name: newBrief.name, status: newBrief.status });

        // Keep the account brief counts fresh.
        await refreshUserAccount();

        return newBrief;
      } catch (error) {
        handleServiceError(error);
        return undefined;
      } finally {
        setIsCreating(false);
      }
    },
    [client, handleServiceError]
  );

  return { createBrief, isCreating };
};

export const useDestroyBrief = () => {
  const client = useApolloClient();
  const handleServiceError = useServiceErrorHandler();
  const [isDestroying, setIsDestroying] = useState(false);

  const destroyBrief = useCallback(
    async (briefId: number): Promise<boolean> => {
      setIsDestroying(true);
      try {
        const deletedBriefId = await briefsManager.destroyBrief(briefId);
        if (!deletedBriefId) {
          return false;
        }

        client.cache.updateQuery<BriefsOverviewResult>({ query: GET_BRIEFS_OVERVIEW }, data =>
          data ? { briefs: data.briefs.filter(b => b.id !== briefId) } : data
        );
        client.cache.evict({ id: client.cache.identify({ __typename: 'Brief', id: briefId }) });
        client.cache.gc();

        // Keep the account brief counts fresh (previously a Redux metadata
        // reducer patched them locally).
        await refreshUserAccount();

        return true;
      } catch (error) {
        handleServiceError(error);
        return false;
      } finally {
        setIsDestroying(false);
      }
    },
    [client, handleServiceError]
  );

  return { destroyBrief, isDestroying };
};

// Per-brief settings mutations with the loading flags the edit panels show.
// Entity-shaped payloads merge through cache normalization; keyword,
// recipient and slack-channel arrays are patched on the Brief entity.
export const useBriefMutations = () => {
  const client = useApolloClient();
  const handleServiceError = useServiceErrorHandler();

  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const [isUpdatingKeywords, setIsUpdatingKeywords] = useState(false);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [isUpdatingSuggestedStories, setIsUpdatingSuggestedStories] = useState(false);
  const [isUpdatingRecipients, setIsUpdatingRecipients] = useState(false);
  const [isUpdatingSlackChannels, setIsUpdatingSlackChannels] = useState(false);

  const modifyBrief = useCallback(
    (briefId: number, fields: Parameters<typeof client.cache.modify>[0]['fields']) => {
      client.cache.modify({
        id: client.cache.identify({ __typename: 'Brief', id: briefId }),
        fields,
      });
    },
    [client]
  );

  const updateBriefName = useCallback(
    async (briefId: number, name: string) => {
      setIsUpdatingName(true);
      try {
        // Payload carries id + name; the cache merges it everywhere.
        await briefsManager.updateBriefName(briefId, name);
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingName(false);
      }
    },
    [handleServiceError]
  );

  const updateLanguage = useCallback(
    async (briefId: number, language: BriefLanguage) => {
      setIsUpdatingLanguage(true);
      try {
        await briefsManager.updateLanguage(briefId, language);
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingLanguage(false);
      }
    },
    [handleServiceError]
  );

  const createBriefKeyword = useCallback(
    async (briefId: number, keyword: string, type: BriefKeywordType) => {
      setIsUpdatingKeywords(true);
      try {
        const briefKeyword = await briefsManager.createBriefKeyword(briefId, keyword, type);
        modifyBrief(briefId, {
          keywords: (existing: any[] = []) => [
            ...existing,
            { ...briefKeyword, __typename: 'BriefKeyword' },
          ],
        });
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingKeywords(false);
      }
    },
    [modifyBrief, handleServiceError]
  );

  const destroyBriefKeyword = useCallback(
    async (briefId: number, keywordId: number) => {
      setIsUpdatingKeywords(true);
      try {
        const deletedKeywordId = await briefsManager.destroyBriefKeyword(briefId, keywordId);
        modifyBrief(briefId, {
          keywords: (existing: any[] = [], { readField }) =>
            existing.filter(
              (ref: Reference | any) =>
                // eslint-disable-next-line eqeqeq
                (readField('id', ref) ?? ref.id) != deletedKeywordId
            ),
        });
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingKeywords(false);
      }
    },
    [modifyBrief, handleServiceError]
  );

  const updateSchedule = useCallback(
    async (briefId: number, hour: number, days: DayOfWeek[]) => {
      setIsUpdatingSchedule(true);
      try {
        await briefsManager.updateBriefSchedule(briefId, days, hour);
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingSchedule(false);
      }
    },
    [handleServiceError]
  );

  const toggleSuggestedStories = useCallback(
    async (briefId: number) => {
      setIsUpdatingSuggestedStories(true);
      try {
        await briefsManager.toggleBriefSuggestedStories(briefId);
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingSuggestedStories(false);
      }
    },
    [handleServiceError]
  );

  const createRecipient = useCallback(
    async (briefId: number, email: string) => {
      setIsUpdatingRecipients(true);
      try {
        const recipient = await briefsManager.createBriefRecipient(briefId, email);
        modifyBrief(briefId, {
          recipients: (existing: any[] = []) => [
            ...existing,
            { ...recipient, __typename: 'BriefRecipient' },
          ],
        });
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingRecipients(false);
      }
    },
    [modifyBrief, handleServiceError]
  );

  const destroyRecipient = useCallback(
    async (briefId: number, recipientId: number) => {
      setIsUpdatingRecipients(true);
      try {
        const deletedRecipientId = await briefsManager.destroyBriefRecipient(briefId, recipientId);
        modifyBrief(briefId, {
          recipients: (existing: any[] = [], { readField }) =>
            existing.filter(
              // eslint-disable-next-line eqeqeq
              (ref: Reference | any) => (readField('id', ref) ?? ref.id) != deletedRecipientId
            ),
        });
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingRecipients(false);
      }
    },
    [modifyBrief, handleServiceError]
  );

  const removeSlackChannel = useCallback(
    async (briefId: number, slackChannelId: number) => {
      setIsUpdatingSlackChannels(true);
      try {
        const removedId = await slackManager.destroySlackChannel(briefId, slackChannelId);
        modifyBrief(briefId, {
          slackChannels: (existing: any[] = [], { readField }) =>
            existing.filter(
              // eslint-disable-next-line eqeqeq
              (ref: Reference | any) => (readField('id', ref) ?? ref.id) != removedId
            ),
        });
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingSlackChannels(false);
      }
    },
    [modifyBrief, handleServiceError]
  );

  return {
    updateBriefName,
    updateLanguage,
    createBriefKeyword,
    destroyBriefKeyword,
    updateSchedule,
    toggleSuggestedStories,
    createRecipient,
    destroyRecipient,
    removeSlackChannel,
    isUpdatingName,
    isUpdatingLanguage,
    isUpdatingKeywords,
    isUpdatingSchedule,
    isUpdatingSuggestedStories,
    isUpdatingRecipients,
    isUpdatingSlackChannels,
  };
};
